import React, {useEffect, useMemo, useState} from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, MenuItem, Select, Stack, TextField,
  Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, PhoneCallIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {AmiRepository} from "../../repositories/AmiRepository";
import {getTarget} from "../../pages/project/dialplan/components/SubProjectFields";
import {subProjectLabel} from "../../utils/subProjects";
import useCallConfirm from "../../hooks/useCallConfirm";

// a phone number: optional leading +, then digits (spaces, dashes, dots and brackets are ignored)
const normalize = (value) => String(value || "").trim().replace(/[\s\-().]/g, "");
const isPhoneNumber = (value) => /^\+?\d{3,15}$/.test(normalize(value));

// Header Start → "Phone Call": the bot calls the number directly. Asterisk originates the call to the number and
// runs it through the chosen dialplan extension (the language's flow), like the old flow layout's Start menu.
export const StartPhoneCallModal = ({open, onClose, project, subProject}) => {
  const subProjects = useMemo(() => project?.subProjects || [], [project?.subProjects]);
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [number, setNumber] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const confirmCall = useCallConfirm("Start this call?");

  // enabled extensions only (disabled ones are not dialable), each with the language it runs
  useEffect(() => {
    if (!open || !project?.id) return;
    setLoading(true);
    AmiRepository.listExtensions(project.id)
      .then((res) => {
        const enabled = (res?.data?.extensions || [])
          .filter(ext => ext?.enabled)
          .map(ext => ({...ext, subProject: subProjects.find(item => item.id === getTarget(ext, subProjects).subProjectId)}))
          .sort((a, b) => String(a.extension).localeCompare(String(b.extension), undefined, {numeric: true}));
        setExtensions(enabled);
        // preselect the current language's first extension
        const own = enabled.find(ext => ext.subProject?.id === subProject?.id) || (enabled.length === 1 ? enabled[0] : null);
        setSelected(own ? String(own.extension) : "");
      })
      .catch(() => {
        setExtensions([]);
        toast.error("Failed to load dialplan extensions");
      })
      .finally(() => setLoading(false));
  }, [open, project?.id, subProject?.id, subProjects]);

  useEffect(() => {
    if (!open) {
      setNumber("");
      setTouched(false);
      setSubmitting(false);
    }
  }, [open]);

  const extension = extensions.find(ext => String(ext.extension) === selected);
  const valid = isPhoneNumber(number);

  const call = async () => {
    setTouched(true);
    if (!extension || !valid) return;
    const toNumber = normalize(number);
    const confirmed = await confirmCall({title: `Call ${toNumber}?`, subtitle: `Via dialplan ${extension.extension}`});
    if (!confirmed) return;

    // shown to the callee: the extension's language outbound caller ID
    const core = (extension.subProject || subProject)?.coreConfiguration || {};
    setSubmitting(true);
    AmiRepository.originateCall(project.id, {
      to_number: toNumber,
      extension: extension.extension,
      caller_id: core.outbound_call || core.redirect_vdn || undefined,
      timeout: 30000,
      async: true,
      variables: {},
    })
      .then(() => {
        toast.success(`Calling ${toNumber}…`);
        onClose();
      })
      .catch((error) => toast.error(error?.response?.data?.error || error?.message || "Failed to start the call"))
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth={"xs"} fullWidth PaperProps={{sx: {borderRadius: 4, overflow: "hidden"}}}>
      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{px: 3, pt: 2.5, pb: 1.5}}>
        <Stack direction={"row"} alignItems={"center"} gap={1.25}>
          <Box sx={{width: 38, height: 38, borderRadius: "12px", display: "grid", placeItems: "center", bgcolor: "rgba(98,88,232,0.1)", color: "#6258E8"}}>
            <HugeiconsIcon icon={PhoneCallIcon} size={20} strokeWidth={1.8}/>
          </Box>
          <Typography fontWeight={700} fontSize={"1.05rem"}>Start a Phone Call</Typography>
        </Stack>
        <IconButton size={"small"} onClick={onClose}><HugeiconsIcon icon={Cancel01Icon} size={18}/></IconButton>
      </Stack>

      <DialogContent sx={{pt: 0.5}}>
        <Stack gap={2}>
          <Stack gap={0.75}>
            <Typography fontWeight={600} fontSize={"0.85rem"}>Dialplan</Typography>
            <Select
              fullWidth size={"small"} displayEmpty value={selected}
              onChange={(e) => setSelected(e.target.value)}
              disabled={loading || extensions.length === 0}
            >
              <MenuItem value={""} disabled>
                {loading ? "Loading active dialplans…" : extensions.length === 0 ? "No active dialplans" : "Select a dialplan"}
              </MenuItem>
              {extensions.map(ext => (
                <MenuItem key={ext.id || ext.extension} value={String(ext.extension)}>
                  {ext.extension}
                  {ext.subProject ? ` — ${subProjectLabel(ext.subProject)}` : ""}
                  {ext.description ? ` · ${ext.description}` : ""}
                </MenuItem>
              ))}
            </Select>
            {!loading && extensions.length === 0 && (
              <Typography fontSize={"0.78rem"} color={"text.secondary"}>
                No enabled dialplan extensions in this project yet — enable one on the Dialplan page first.
              </Typography>
            )}
          </Stack>

          <Stack gap={0.75}>
            <Typography fontWeight={600} fontSize={"0.85rem"}>Phone number</Typography>
            <TextField
              fullWidth size={"small"} autoFocus placeholder={"Enter phone number to call"}
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              onBlur={() => setTouched(true)}
              onKeyDown={(e) => e.key === "Enter" && call()}
              error={touched && Boolean(number) && !valid}
              helperText={touched && Boolean(number) && !valid ? "Enter a valid phone number" : ""}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{px: 3, pb: 3, pt: 1}}>
        <Button onClick={onClose} color={"secondary"} variant={"ghost"}>Cancel</Button>
        <Button
          variant={"contained"} color={"success"}
          disabled={!extension || !valid || submitting}
          startIcon={submitting ? <CircularProgress size={16} color={"inherit"}/> : <HugeiconsIcon icon={PhoneCallIcon} size={18}/>}
          sx={{borderRadius: 2.5, px: 2.5}}
          onClick={call}
        >
          Call
        </Button>
      </DialogActions>
    </Dialog>
  );
};
